import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import UserServices from "../../../services/UserServices"; // Ensure this imports the updateUser method
import Swal from "sweetalert2";
import UseEditModal from './UseEditModal';
import Select from 'react-select';
import UserAddModel from "./UserAddModel";
import UserPending from "./UserPending";
import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";
import * as XLSX from "xlsx";

const downloadUsersExcel = (users, status) => {
  if (!Array.isArray(users) || users.length === 0) {
    Swal.fire("Info", "No users available to download.", "info");
    return false;
  }

  try {
    const rows = [
      [
        "S.No",
        "User Role",
        "Name",
        "Email",
        "Store",
        "Mobile No.",
        "Status"
      ],
      ...users.map((user, index) => [
        index + 1,
        user.role || "",
        user.name || "",
        user.email || "",
        Array.isArray(user.store)
          ? user.store
            .map((store) => store?.name || "")
            .filter(Boolean)
            .join(", ")
          : "",
        String(user.mobileNo || ""),
        user.status || status
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 20 },
      { wch: 28 },
      { wch: 38 },
      { wch: 50 },
      { wch: 18 },
      { wch: 14 }
    ];

    worksheet["!autofilter"] = {
      ref: `A1:G${rows.length}`
    };

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Users"
    );

    const today = new Date();

    const date = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0")
    ].join("-");

    XLSX.writeFile(
      workbook,
      `${status}_Users_${date}.xlsx`
    );

    return true;
  } catch (error) {
    console.error("User Excel download failed:", error);
    Swal.fire("Error", "Unable to download users.", "error");
    return false;
  }
};


const User = () => {
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(true);
  const [gridInsideModal, setGridInsideModal] = useState(false);
  const [sort, setSort] = useState(100); // Number of items per page
  const activePag = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [test, setTest] = useState(0);
  const [selectedItem, setSelectedItem] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [downloadingUsers, setDownloadingUsers] = useState(false);
  const downloadInProgress = useRef(false);

  const loggedInRole = (() => {
    try {
      const loggedInUser = JSON.parse(
        localStorage.getItem("userDetails") || "null"
      );

      return String(loggedInUser?.data?.user?.role || "")
        .trim()
        .toLowerCase();
    } catch {
      return "";
    }
  })();

  const isAccounts = loggedInRole === "accounts";
  const canEditUser = (user) => {
    if (!user?._id) return false;

    const targetRole = String(user.role || "")
      .trim()
      .toLowerCase();

    const isCashUser = [
      "cashmaker",
      "cashchecker",
    ].includes(targetRole);

    return isCashUser ? isAccounts : !isAccounts;
  };

  const canDownloadUsers = ["accounts", "superadmin"].includes(
    loggedInRole
  );

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserServices.getAllUser();
      const allUsers = response?.data?.data || [];

      const filteredActiveUsers = allUsers.filter((user) => {
        const normalizedRole = String(user.role || "")
          .trim()
          .toLowerCase();

        const isCashUser = [
          "cashmaker",
          "cashchecker",
          "accounts"
        ].includes(normalizedRole);

        return (
          user.status === "active" &&
          (!isAccounts || isCashUser)
        );
      });

      setActiveUsers(filteredActiveUsers);

      setActiveUsers(filteredActiveUsers);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [test]);

  useEffect(() => {
    if (activeTab === "active") {
      fetchUsers();
    }
  }, [activeTab]);

  // User search
  const filteredSearch = activeUsers.filter(user =>
    user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user?.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user?.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    activePag.current = 0;
  }, [searchTerm]);

  // Options for row selections
  const options = [
    { value: '5', label: '5' },
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '50', label: '50' },
    { value: '100', label: '100' }
  ];

  // Pagination setup
  useEffect(() => {
    if (activeUsers.length > 0) {
      activePag.current = 0;
    }
  }, [activeUsers]);

  // Calculate total pages
  const pagination = Math.ceil(filteredSearch.length / sort);
  const onClick = (i) => {
    activePag.current = i;
    setTest(prev => prev + 1);
  };

  // Handle deleting user
  function handleDelete(e, user) {
    Swal.fire({
      title: 'Are you sure to delete?',
      text: "You will not be able to recover this User Again!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dd6b55',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        UserServices.deleteUser(user._id).then(() => {
          setTest(user._id);
          Swal.fire('Deleted!', 'User has been deleted.', 'success');
        }).catch((error) => {
          Swal.fire('Error!', 'There was a problem deleting the user.', 'error');
        });
      }
    });
  }

  const handleStatusToggle = async (user) => {
    const newStatus = user.status === "active" ? "inactive" : "active";

    try {
      await UserServices.updateUser(user._id, {
        status: newStatus, // ✅ ONLY send required field
      });

      setTest(prev => prev + 1); // re-fetch users
      Swal.fire('Success!', `User status updated to ${newStatus}.`, 'success');
    } catch (error) {
      console.error(error);
      Swal.fire('Error!', 'There was a problem updating the user status.', 'error');
    }
  };


  const handleEditClick = (item) => {
    if (!canEditUser(item)) return;

    setSelectedItem(item);
    setGridInsideModal(true);
  };

  const handleDownloadUsers = async () => {
    if (!canDownloadUsers || downloadInProgress.current) return;

    downloadInProgress.current = true;
    setDownloadingUsers(true);

    try {
      // Give React time to display the spinner.
      await new Promise((resolve) => setTimeout(resolve, 50));

      const success = downloadUsersExcel(
        filteredSearch,
        "Active"
      );

      if (success) {
        Swal.fire({
          icon: "success",
          title: "Excel File Ready",
          text: "The file has been generated. Please check your browser downloads.",
          timer: 2500,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("User download failed:", error);

      Swal.fire(
        "Error",
        "Unable to download users. Please try again.",
        "error"
      );
    } finally {
      downloadInProgress.current = false;
      setDownloadingUsers(false);
    }
  };

  if (loading) {
    return (
      <Loader />
    );
  }


  return (
    <>
      <UseEditModal
        admin={selectedItem}
        show={gridInsideModal}
        test={setTest}
        handleClose={() => setGridInsideModal(false)}
      />

      <UserAddModel
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        test={setTest}
      />


      {activeTab === "active" ? (
        <div className="row">
          <div className="col-xl-12">
            <div className="filter cm-content-box box-primary">
              <div className={`content-title`}>
                <div className="cpa">
                  <i className="fas fa-filter me-2"></i>Filter
                </div>
                <div className="tools">
                  <Link to="#" className={`SlideToolHeader ${open ? 'collapse' : 'expand'}`}
                    onClick={() => setOpen(!open)}
                  >
                    <i className="fas fa-angle-up"></i>
                  </Link>
                </div>
              </div>
              <Collapse in={open}>
                <div className="cm-content-body form excerpt">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-xl-3 col-xxl-6">
                        <input type="text"
                          className="form-control mb-xl-0 mb-3"
                          id="exampleFormControlInput1"
                          placeholder="Search by name, email, or role..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            activePag.current = 0;
                          }}
                        />
                      </div>
                      <div className="col-xl-3 col-xxl-6">
                        <Select
                          placeholder="Select row to display in User Table"
                          value={options.find(option => option.value === sort)}
                          onChange={(selectedOption) => {
                            setSort(Number(selectedOption.value));
                            activePag.current = 0;
                          }}
                          isSearchable={false}
                          options={options}
                          className="custom-react-select mb-3 mb-xxl-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
            <div className="filter cm-content-box box-primary">
              <div className="content-title user-header-line">
                <div className="cpa">
                  <i className="fa fa-list me-2" aria-hidden="true" />
                  Active User
                  <span className="badge bg-orange ms-2">
                    {filteredSearch.length}
                  </span>
                </div>

                <div className="user-header-tabs">
                  <button
                    type="button"
                    className={activeTab === "active" ? "is-active" : ""}
                    onClick={() => setActiveTab("active")}
                  >
                    Active Users
                  </button>

                  <button
                    type="button"
                    className={activeTab === "inactive" ? "is-active" : ""}
                    onClick={() => setActiveTab("inactive")}
                  >
                    Pending Users
                  </button>
                </div>

                <div className="user-header-icons">
                  {canDownloadUsers && (
                    <button
                      type="button"
                      className="user-header-icon"
                      onClick={handleDownloadUsers}
                      disabled={
                        downloadingUsers || filteredSearch.length === 0
                      }
                      title={
                        downloadingUsers ? "Preparing Excel…" : "Download Excel"
                      }
                      aria-label={
                        downloadingUsers ? "Preparing Excel…" : "Download Excel"
                      }
                      aria-busy={downloadingUsers}
                    >
                      {downloadingUsers ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          aria-hidden="true"
                        />
                      ) : (
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 3v12" />
                          <path d="m7 10 5 5 5-5" />
                          <path d="M5 16v4h14v-4" />
                        </svg>
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    className="user-header-icon user-header-add"
                    onClick={() => setShowAddModal(true)}
                    title="Add New User"
                    aria-label="Add New User"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>

                  <div className="tools">
                    <Link
                      to="#"
                      className={`SlideToolHeader ${open2 ? "collapse" : "expand"
                        }`}
                      onClick={(event) => {
                        event.preventDefault();
                        setOpen2((previous) => !previous);
                      }}
                      title={open2 ? "Hide table" : "Show table"}
                      aria-label={open2 ? "Hide table" : "Show table"}
                      aria-expanded={open2}
                    >
                      <i className="fas fa-angle-up" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
              <Collapse in={open2}>
                <div className="cm-content-body form excerpt">
                  <div className="card-body">


                    <div className="table-responsive">
                      <div id="content_wrapper" className="dataTables_wrapper no-footer">
                        <div style={{
                          overflowX: 'auto',
                          overflowY: 'auto',
                          maxHeight: '400px',
                          whiteSpace: 'nowrap',
                          position: 'relative',   // ✅ ADD THIS
                          zIndex: 0               // ✅ ADD THIS
                        }}>
                          <table
                            className="table table-bordered table-responsive-lg table-striped table-condensed flip-content user-list-table"
                            id="active-users-data"
                          >
                            <thead
                              style={{
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,              // ✅ LOWER than sidebar
                              }}
                            >
                              <tr>
                                <th>S.No</th>
                                <th>User Details</th>
                                <th>Store</th>
                                <th>Mobile No.</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredSearch.length > 0 ? (
                                filteredSearch
                                  .slice(activePag.current * sort, (activePag.current + 1) * sort)
                                  .map((user, index) => (
                                    <tr key={index}>
                                      <td>{activePag.current * sort + index + 1}</td>
                                      <td className="user-details-cell">
                                        <div className="user-details-content">
                                          <div className="mb-1">
                                            <strong>Role:</strong> {user?.role || "—"}
                                          </div>

                                          <div className="mb-1">
                                            <strong>Name:</strong> {user?.name || "—"}
                                          </div>

                                          <div>
                                            <strong>Email:</strong> {user?.email || "—"}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="scrollable-description">
                                        {user?.store?.length > 0
                                          ? user.store.map(store => store.name).join(', ')
                                          : 'No Stores'}
                                      </td>
                                      <td>{user?.mobileNo}</td>
                                      <td>
                                        <span
                                          onClick={() => handleStatusToggle(user)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <i
                                            className={`fa ${user.status === 'active' ? 'fa-circle text-success' : 'fa-circle text-danger'}`}
                                            title={user.status === 'active' ? 'Active' : 'Inactive'}
                                          ></i>
                                        </span>
                                      </td>

                                      <td className="text-center">
                                        <button
                                          type="button"
                                          className="btn btn-primary btn-xs sharp"
                                          disabled={!canEditUser(user)}
                                          onClick={() => handleEditClick(user)}
                                          title={
                                            canEditUser(user)
                                              ? "Edit User"
                                              : "You do not have permission to edit this user"
                                          }
                                          aria-label="Edit User"
                                          style={{
                                            opacity: canEditUser(user) ? 1 : 0.4,
                                            cursor: canEditUser(user) ? "pointer" : "not-allowed"
                                          }}
                                        >
                                          <i className="fa fa-pencil" aria-hidden="true" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center">
                                    No Active Users Found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                          <div className="dataTables_info">
                            {filteredSearch.length > 0 ? (
                              <>
                                Showing {activePag.current * sort + 1} to{" "}
                                {Math.min(filteredSearch.length, (activePag.current + 1) * sort)} of{" "}
                                {filteredSearch.length} entries
                              </>
                            ) : (
                              <>Showing 0 to 0 of 0 entries</>
                            )}
                          </div>
                          {/* <div className="dataTables_paginate paging_simple_numbers">
                            <Link
                              className="paginate_button previous"
                              to="#"
                              onClick={() =>
                                activePag.current > 0 && onClick(activePag.current - 1)
                              }
                            >
                              <i className="fa fa-angle-double-left" aria-hidden="true"></i>
                            </Link>
                            <span>
                              {Array.from({ length: pagination }, (_, i) => (
                                <Link
                                  key={i}
                                  to="#"
                                  className={`paginate_button ${activePag.current === i ? "current" : ""}`}
                                  onClick={() => onClick(i)}
                                >
                                  {i + 1}
                                </Link>
                              ))}
                            </span>
                            <Link
                              className="paginate_button next"
                              to="#"
                              onClick={() =>
                                activePag.current < pagination - 1 && onClick(activePag.current + 1)
                              }
                            >
                              <i className="fa fa-angle-double-right" aria-hidden="true"></i>
                            </Link>
                          </div> */}

                          <Pagination activePage={activePag.current}
                            totalPages={pagination}
                            onPageChange={onClick} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
          </div>
        </div>
      ) : (
        <UserPending
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setShowAddModal={setShowAddModal}
        />
      )}
    </>
  );
};

export default User;
